import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  ReactNode,
  useState,
} from 'react'
import userMediaApi from '../client/user-media-api'
import { useAuth } from './AuthContext'
import { readMediaStateCache, writeMediaStateCache } from './media-state.cache'
import { normalizeMediaState } from './media-state.normalizer'
import { initialMediaState, mediaStateReducer } from './media-state.reducer'
import {
  MediaStateItem,
  MediaStateResponse,
  HistoryEpisode,
} from './media-state.types'
import { useSnackbar } from './snackbar-context'

type ItemMeta = {
  title: string
  posterPath?: string
  releaseYear?: number
  genres?: string[]
}

type EpisodeMeta = {
  id: number
  name: string
  episodeNumber: number
  seasonNumber: number
  airDate?: string
}

type EpisodeContextMeta = {
  showId: number
  showTitle: string
  posterUrl?: string
  airDate?: string
  genres?: string[]
  episodes: EpisodeMeta[]
}

type MediaStateContextType = {
  mediaState: MediaStateResponse | null
  isLoaded: boolean
  isFetching: boolean
  isAuthenticated: boolean
  favoritesSet: Set<number>
  watchlistSet: Set<number>
  historySet: Set<number>
  watchedEpisodesSet: Set<number>
  isFavoritePending: (tmdbId: number) => boolean
  isWatchlistPending: (tmdbId: number) => boolean
  isHistoryPending: (tmdbId: number) => boolean
  isEpisodePending: (episodeId: number) => boolean
  isSeasonPending: (showId: number, seasonNumber: number) => boolean
  toggleFavorite: (
    tmdbId: number,
    mediaType: 'movie' | 'show',
    meta: ItemMeta,
  ) => Promise<void>
  toggleWatchlist: (
    tmdbId: number,
    mediaType: 'movie' | 'show',
    meta: ItemMeta,
  ) => Promise<void>
  toggleHistory: (
    tmdbId: number,
    mediaType: 'movie' | 'show',
    meta: ItemMeta,
  ) => Promise<void>
  toggleEpisodeWatched: (
    episode: EpisodeMeta,
    context: EpisodeContextMeta,
  ) => Promise<void>
  setShowEpisodesWatched: (
    episodeIds: number[],
    context: EpisodeContextMeta,
  ) => Promise<void>
  markAllEpisodesWatched: (showId: number) => Promise<void>
}

const MediaStateContext = createContext<MediaStateContextType | undefined>(
  undefined,
)

const MediaStateProvider = ({ children }: { children: ReactNode }) => {
  const { getAccessToken, isAuthenticated, isAuthReady, user } = useAuth()
  const [state, dispatch] = useReducer(mediaStateReducer, initialMediaState)
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set())
  const [isFetching, setIsFetching] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const fetchingRef = useRef(false)

  const media = state.data

  const favoritesSet = useMemo(
    () => new Set(media?.favorites.map((f) => f.tmdbId) ?? []),
    [media],
  )
  const watchlistSet = useMemo(
    () => new Set(media?.watchlist.map((w) => w.tmdbId) ?? []),
    [media],
  )
  const historySet = useMemo(
    () => new Set(media?.history.map((h) => h.tmdbId) ?? []),
    [media],
  )
  const watchedEpisodesSet = useMemo(() => {
    const set = new Set<number>()
    media?.history.forEach((h) =>
      h.episodes?.forEach((ep) => set.add(ep.episodeId)),
    )
    return set
  }, [media])

  const addPending = (key: string) =>
    setPendingKeys((prev: Set<string>) => {
      const next = new Set(prev)
      next.add(key)
      return next
    })
  const removePending = (key: string) =>
    setPendingKeys((prev: Set<string>) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })

  const isFavoritePending = useCallback(
    (tmdbId: number) => pendingKeys.has(`fav-${tmdbId}`),
    [pendingKeys],
  )
  const isWatchlistPending = useCallback(
    (tmdbId: number) => pendingKeys.has(`wl-${tmdbId}`),
    [pendingKeys],
  )
  const isHistoryPending = useCallback(
    (tmdbId: number) => pendingKeys.has(`hist-${tmdbId}`),
    [pendingKeys],
  )
  const isEpisodePending = useCallback(
    (episodeId: number) => pendingKeys.has(`ep-${episodeId}`),
    [pendingKeys],
  )
  const isSeasonPending = useCallback(
    (showId: number, seasonNumber: number) =>
      pendingKeys.has(`season-${showId}-${seasonNumber}`),
    [pendingKeys],
  )

  const fetchAndApply = useCallback(async () => {
    if (!isAuthReady || fetchingRef.current) return
    fetchingRef.current = true
    setIsFetching(true)
    try {
      const token = await getAccessToken()
      const res = await userMediaApi.getState(token)
      const normalized = normalizeMediaState(res.data)
      dispatch({ type: 'set', payload: normalized })
      writeMediaStateCache(normalized)
    } catch {
      dispatch({ type: 'loaded' })
    } finally {
      fetchingRef.current = false
      setIsFetching(false)
    }
  }, [getAccessToken, isAuthReady])

  // Initial load: cache first, then fetch fresh data once auth is READY (a
  // token exists). Keying on isAuthReady instead of isAuthenticated is the fix
  // for "logged in but shows 0 movies": on a fresh tab isAuthenticated flips
  // true before the token getter is wired, and the old effect fetched during
  // that window, 401'd, and left empty data with no retry.
  useEffect(() => {
    // Load from cache for immediate display
    if (!state.loaded) {
      const cached = readMediaStateCache()
      if (cached) {
        dispatch({ type: 'set', payload: cached })
      }
    }

    if (isAuthReady) {
      fetchAndApply().catch(() => dispatch({ type: 'loaded' }))
    } else if (!isAuthenticated && !state.loaded) {
      // Genuinely logged out (not just still settling): nothing to fetch.
      dispatch({ type: 'loaded' })
    }
  }, [isAuthReady, isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to update cache after local state changes
  const updateCache = useCallback(() => {
    if (state.data) {
      writeMediaStateCache(state.data)
    }
  }, [state.data])

  const ensureAuth = useCallback(async () => {
    if (!isAuthenticated || !user) {
      enqueueSnackbar('Please sign in to continue', { variant: 'error' })
      return null
    }
    try {
      const token = await getAccessToken()
      return { token, userId: user.id }
    } catch {
      enqueueSnackbar('Unable to refresh session', { variant: 'error' })
      return null
    }
  }, [enqueueSnackbar, getAccessToken, isAuthenticated, user])

  const toggleFavorite = useCallback(
    async (tmdbId: number, mediaType: 'movie' | 'show', meta: ItemMeta) => {
      const auth = await ensureAuth()
      if (!auth) return
      const pendingKey = `fav-${tmdbId}`
      addPending(pendingKey)
      const normalizedType = mediaType === 'movie' ? 'Movie' : 'Show'
      const now = new Date().toISOString()
      let successMessage = ''
      try {
        if (favoritesSet.has(tmdbId)) {
          await userMediaApi.deleteFavoriteByTmdb(tmdbId, auth.token)
          dispatch({ type: 'remove_favorite', payload: tmdbId })
          successMessage = 'Removed From Favorites'
        } else {
          await userMediaApi.addFavorite(
            {
              Favorites_UserId: auth.userId,
              Favorites_MediaType: normalizedType,
              Favorites_TmdbMediaId: tmdbId,
              Favorites_Title: meta.title,
              Favorites_PosterPath: meta.posterPath,
              Favorites_ReleaseYear: meta.releaseYear,
              Favorites_GenresJson: JSON.stringify(meta.genres ?? []),
              Favorites_CreatedOnUtc: now,
              Favorites_CreatedBy: auth.userId,
              Favorites_ModifiedOnUtc: now,
              Favorites_ModifiedBy: auth.userId,
            },
            auth.token,
          )
          const newItem: MediaStateItem = {
            tmdbId,
            mediaType: normalizedType,
            title: meta.title,
            posterPath: meta.posterPath,
            releaseYear: meta.releaseYear,
            genres: meta.genres ?? [],
          }
          dispatch({ type: 'add_favorite', payload: newItem })
          successMessage = 'Added To Favorites'
        }
        updateCache()
        if (successMessage) {
          enqueueSnackbar(successMessage, { variant: 'success' })
        }
      } finally {
        removePending(pendingKey)
      }
    },
    [ensureAuth, favoritesSet, updateCache, enqueueSnackbar],
  )

  const toggleWatchlist = useCallback(
    async (tmdbId: number, mediaType: 'movie' | 'show', meta: ItemMeta) => {
      const auth = await ensureAuth()
      if (!auth) return
      const pendingKey = `wl-${tmdbId}`
      addPending(pendingKey)
      const normalizedType = mediaType === 'movie' ? 'Movie' : 'Show'
      const now = new Date().toISOString()
      let successMessage = ''
      try {
        if (watchlistSet.has(tmdbId)) {
          await userMediaApi.deleteWatchlistByTmdb(tmdbId, auth.token)
          dispatch({ type: 'remove_watchlist', payload: tmdbId })
          successMessage = 'Removed From Watchlist'
        } else {
          await userMediaApi.addWatchlist(
            {
              Watchlist_UserId: auth.userId,
              Watchlist_MediaType: normalizedType,
              Watchlist_TmdbMediaId: tmdbId,
              Watchlist_Title: meta.title,
              Watchlist_PosterPath: meta.posterPath,
              Watchlist_ReleaseYear: meta.releaseYear,
              Watchlist_GenresJson: JSON.stringify(meta.genres ?? []),
              Watchlist_CreatedOnUtc: now,
              Watchlist_CreatedBy: auth.userId,
              Watchlist_ModifiedOnUtc: now,
              Watchlist_ModifiedBy: auth.userId,
            },
            auth.token,
          )
          const newItem: MediaStateItem = {
            tmdbId,
            mediaType: normalizedType,
            title: meta.title,
            posterPath: meta.posterPath,
            releaseYear: meta.releaseYear,
            genres: meta.genres ?? [],
          }
          dispatch({ type: 'add_watchlist', payload: newItem })
          successMessage = 'Added To Watchlist'
        }
        updateCache()
        if (successMessage) {
          enqueueSnackbar(successMessage, { variant: 'success' })
        }
      } finally {
        removePending(pendingKey)
      }
    },
    [ensureAuth, updateCache, watchlistSet, enqueueSnackbar],
  )

  const toggleHistory = useCallback(
    async (tmdbId: number, mediaType: 'movie' | 'show', meta: ItemMeta) => {
      const auth = await ensureAuth()
      if (!auth) return
      const pendingKey = `hist-${tmdbId}`
      addPending(pendingKey)
      const normalizedType = mediaType === 'movie' ? 'Movie' : 'Show'
      const now = new Date().toISOString()
      let successMessage = ''
      try {
        if (historySet.has(tmdbId)) {
          await userMediaApi.deleteHistoryByTmdb(tmdbId, auth.token)
          dispatch({ type: 'remove_history', payload: tmdbId })
          successMessage = 'Removed From History'
        } else {
          await userMediaApi.addHistory(
            {
              History_UserId: auth.userId,
              History_MediaType: normalizedType,
              History_TmdbMediaId: tmdbId,
              History_Title: meta.title,
              History_PosterPath: meta.posterPath,
              History_ReleaseYear: meta.releaseYear,
              History_GenresJson: JSON.stringify(meta.genres ?? []),
              History_WatchedOnUtc: now,
              History_CreatedOnUtc: now,
              History_CreatedBy: auth.userId,
              History_ModifiedOnUtc: now,
              History_ModifiedBy: auth.userId,
            },
            auth.token,
          )
          const newItem: MediaStateItem = {
            tmdbId,
            mediaType: normalizedType,
            title: meta.title,
            posterPath: meta.posterPath,
            releaseYear: meta.releaseYear,
            genres: meta.genres ?? [],
          }
          dispatch({ type: 'add_history', payload: newItem })
          if (watchlistSet.has(tmdbId)) {
            await userMediaApi.deleteWatchlistByTmdb(tmdbId, auth.token)
            dispatch({ type: 'remove_watchlist', payload: tmdbId })
          }
          successMessage = 'Marked as Watched'
        }
        updateCache()
        if (successMessage) {
          enqueueSnackbar(successMessage, { variant: 'success' })
        }
      } finally {
        removePending(pendingKey)
      }
    },
    [ensureAuth, updateCache, historySet, watchlistSet, enqueueSnackbar],
  )

  const toggleEpisodeWatched = useCallback(
    async (episode: EpisodeMeta, context: EpisodeContextMeta) => {
      const auth = await ensureAuth()
      if (!auth) return
      const pendingKey = `ep-${episode.id}`
      addPending(pendingKey)
      const episodeId = episode.id
      const showId = context.showId
      const payload = {
        episodeId,
        season: episode.seasonNumber,
        episode: episode.episodeNumber,
        title: episode.name,
        showTitle: context.showTitle,
        posterPath: context.posterUrl,
        releaseYear:
          context.airDate && context.airDate.length >= 4
            ? Number(context.airDate.substring(0, 4))
            : undefined,
        genres: context.genres ?? [],
      }
      let successMessage = ''
      try {
        if (watchedEpisodesSet.has(episodeId)) {
          await userMediaApi.removeEpisodeFromShowHistory(
            showId,
            episodeId,
            auth.token,
          )
          dispatch({ type: 'remove_episode', payload: { showId, episodeId } })
          successMessage = 'Episode removed from watched'
        } else {
          await userMediaApi.addEpisodeToShowHistory(
            showId,
            payload,
            auth.token,
          )
          const historyEpisode: HistoryEpisode = {
            episodeId,
            season: episode.seasonNumber,
            episode: episode.episodeNumber,
            episodeTitle: episode.name,
          }
          const showMeta: MediaStateItem = {
            tmdbId: showId,
            mediaType: 'Show',
            title: context.showTitle,
            posterPath: context.posterUrl,
            releaseYear: payload.releaseYear,
            genres: context.genres ?? [],
          }
          dispatch({
            type: 'add_episode',
            payload: { showId, episode: historyEpisode, showMeta },
          })
          successMessage = 'Episode marked as watched'
        }
        updateCache()
        if (successMessage) {
          enqueueSnackbar(successMessage, { variant: 'success' })
        }
      } catch {
        enqueueSnackbar('Failed to update episode', { variant: 'error' })
      } finally {
        removePending(pendingKey)
      }
    },
    [ensureAuth, updateCache, watchedEpisodesSet, enqueueSnackbar],
  )

  const setShowEpisodesWatched = useCallback(
    async (episodeIds: number[], context: EpisodeContextMeta) => {
      const auth = await ensureAuth()
      if (!auth) return
      const seasonNumber = context.episodes[0]?.seasonNumber
      if (!seasonNumber) return
      const pendingKey = `season-${context.showId}-${seasonNumber}`
      addPending(pendingKey)

      let successMessage = ''
      try {
        if (episodeIds.length === 0) {
          await userMediaApi.removeSeasonFromShowHistory(
            context.showId,
            seasonNumber,
            auth.token,
          )
          dispatch({
            type: 'remove_season_episodes',
            payload: { showId: context.showId, seasonNumber },
          })
          successMessage = 'Season Removed From Watched'
        } else {
          const episodesPayload = context.episodes
            .filter((ep) => episodeIds.includes(ep.id))
            .map((ep) => ({
              episodeId: ep.id,
              episode: ep.episodeNumber,
              title: ep.name,
            }))

          const releaseYear =
            context.airDate && context.airDate.length >= 4
              ? Number(context.airDate.substring(0, 4))
              : undefined

          await userMediaApi.addSeasonToShowHistory(
            context.showId,
            {
              season: seasonNumber,
              episodes: episodesPayload,
              showTitle: context.showTitle,
              posterPath: context.posterUrl,
              releaseYear,
              genres: context.genres ?? [],
            },
            auth.token,
          )
          const historyEpisodes: HistoryEpisode[] = context.episodes
            .filter((ep) => episodeIds.includes(ep.id))
            .map((ep) => ({
              episodeId: ep.id,
              season: seasonNumber,
              episode: ep.episodeNumber,
              episodeTitle: ep.name,
            }))
          const showMeta: MediaStateItem = {
            tmdbId: context.showId,
            mediaType: 'Show',
            title: context.showTitle,
            posterPath: context.posterUrl,
            releaseYear,
            genres: context.genres ?? [],
          }
          dispatch({
            type: 'add_season_episodes',
            payload: {
              showId: context.showId,
              episodes: historyEpisodes,
              showMeta,
            },
          })
          successMessage = 'Season marked as watched'
        }

        updateCache()
        if (successMessage) {
          enqueueSnackbar(successMessage, { variant: 'success' })
        }
      } catch {
        enqueueSnackbar('Failed to update season', { variant: 'error' })
      } finally {
        removePending(pendingKey)
      }
    },
    [ensureAuth, updateCache, enqueueSnackbar],
  )

  // Marks every aired episode of a show as watched. The server decides which
  // episodes qualify (Specials and unaired excluded), so afterwards we refetch
  // the whole media state instead of patching it locally.
  const markAllEpisodesWatched = useCallback(
    async (showId: number) => {
      const auth = await ensureAuth()
      if (!auth) return
      const pendingKey = `hist-${showId}`
      addPending(pendingKey)
      try {
        const res = await userMediaApi.addAllEpisodesToShowHistory(
          showId,
          auth.token,
        )
        await fetchAndApply()
        const { episodesMarked } = res.data
        enqueueSnackbar(
          episodesMarked > 0
            ? `Marked ${episodesMarked} episode${episodesMarked === 1 ? '' : 's'} as watched`
            : 'No aired episodes to mark',
          { variant: episodesMarked > 0 ? 'success' : 'info' },
        )
      } catch {
        enqueueSnackbar('Failed to mark episodes as watched', {
          variant: 'error',
        })
      } finally {
        removePending(pendingKey)
      }
    },
    [ensureAuth, fetchAndApply, enqueueSnackbar],
  )

  const value = useMemo<MediaStateContextType>(
    () => ({
      mediaState: media,
      isLoaded: state.loaded,
      isFetching,
      isAuthenticated,
      favoritesSet,
      watchlistSet,
      historySet,
      watchedEpisodesSet,
      isFavoritePending,
      isWatchlistPending,
      isHistoryPending,
      isEpisodePending,
      isSeasonPending,
      toggleFavorite,
      toggleWatchlist,
      toggleHistory,
      toggleEpisodeWatched,
      setShowEpisodesWatched,
      markAllEpisodesWatched,
    }),
    [
      favoritesSet,
      watchlistSet,
      historySet,
      watchedEpisodesSet,
      media,
      state.loaded,
      isFetching,
      isAuthenticated,
      toggleFavorite,
      toggleWatchlist,
      toggleHistory,
      toggleEpisodeWatched,
      setShowEpisodesWatched,
      markAllEpisodesWatched,
      isFavoritePending,
      isWatchlistPending,
      isHistoryPending,
      isEpisodePending,
      isSeasonPending,
    ],
  )

  return (
    <MediaStateContext.Provider value={value}>
      {children}
    </MediaStateContext.Provider>
  )
}

export const useMediaState = () => {
  const ctx = useContext(MediaStateContext)
  if (!ctx) {
    throw new Error('useMediaState must be used within MediaStateProvider')
  }
  return ctx
}

export default MediaStateProvider
